import { usePreview, termStatus, logoSource, SILVER } from "@/lib/bannerLayout";
import { Lock } from "lucide-react";

export function BannerLogo({ partner, previewPath, className = "", testid }) {
  const srcInfo = logoSource(partner);
  const src = usePreview(srcInfo?.id || null, previewPath);
  if (!src) return null;
  if (!srcInfo.tint) return <img src={src} alt={partner.name} draggable={false} className={`max-h-full max-w-full object-contain pointer-events-none ${className}`} data-testid={testid} />;
  const m = `url(${src}) center / contain no-repeat`;
  return <span role="img" aria-label={partner.name} className={`block h-full w-full pointer-events-none ${className}`} style={{ backgroundColor: SILVER, WebkitMask: m, mask: m }} data-testid={testid} />;
}

const STATUS = {
  expired: "border-red-400 border-dashed bg-red-900/30",
  ending: "border-amber-300",
  planned: "border-sky-300 border-dashed bg-sky-400/25",
};
const BADGE = { expired: "bg-red-600", ending: "bg-amber-500", planned: "bg-sky-600" };

const pos = (b) => ({ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%` });

function Handle({ onDown, testid }) {
  return <span onPointerDown={onDown} className="absolute -right-1.5 -bottom-1.5 h-3.5 w-3.5 rounded-sm bg-white border border-slate-500 cursor-nwse-resize" data-testid={testid} />;
}

export function SlotBox({ slot, num, partner, today, selected, warn, onDown, previewPath, readOnly }) {
  const has = !!logoSource(partner);
  const st = termStatus(partner, today);
  const free = !partner;
  return (
    <div style={{ ...pos(slot), touchAction: "none" }} onPointerDown={(e) => onDown(e, "move")}
      className={`absolute rounded-md border-2 ${readOnly ? "" : "cursor-move"} select-none flex items-center justify-center transition-[box-shadow,background-color] duration-150
        ${free ? "border-dashed border-white/60 bg-white/5 hover:bg-white/10" : st ? STATUS[st.key] : "border-transparent hover:border-white/30"}
        ${warn ? "!border-red-500 !border-solid bg-red-500/20" : ""} ${selected ? "ring-2 ring-white ring-offset-2 ring-offset-[#005448] z-20" : "z-10"}`}
      title={readOnly && partner ? partner.name : undefined} data-testid={`banner-slot-${num}`}>
      <span className="absolute left-1 top-1 h-4 min-w-4 px-1 rounded-full bg-black/40 text-white text-[10px] leading-4 text-center" data-testid={`banner-slot-num-${num}`}>{num}</span>
      {free && <span className="text-white/80 text-xs font-semibold tracking-wide uppercase" data-testid={`banner-slot-free-${num}`}>Frei</span>}
      {!free && has && <span className="h-full w-full"><BannerLogo partner={partner} previewPath={previewPath} className={st?.key === "expired" ? "opacity-40" : ""} testid={`banner-slot-logo-${num}`} /></span>}
      {!free && !has && <span className="px-1 text-center text-white text-[11px] leading-tight font-medium">{partner.name}<span className="block text-amber-300 text-[10px]">Logo fehlt</span></span>}
      {st && <span className={`absolute -bottom-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1.5 text-[10px] font-semibold text-white ${BADGE[st.key]}`} data-testid={`banner-slot-status-${num}`}>{st.label}</span>}
      {selected && <Handle onDown={(e) => onDown(e, "resize")} testid={`banner-slot-resize-${num}`} />}
    </div>
  );
}

export function BlockBox({ block, idx, selected, onDown, readOnly }) {
  return (
    <div style={{ ...pos(block), touchAction: "none", backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.22) 0 6px, transparent 6px 12px)" }}
      onPointerDown={(e) => onDown(e, "move")}
      className={`absolute rounded-sm bg-black/35 border border-white/50 ${readOnly ? "" : block.locked ? "cursor-not-allowed" : "cursor-move"} select-none flex items-center justify-center ${selected ? "ring-2 ring-amber-300 z-20" : "z-[5]"}`}
      data-testid={`banner-block-${idx}`}>
      <span className="px-1 text-center text-[10px] font-bold uppercase tracking-wider text-white/90 bg-black/40 rounded inline-flex items-center gap-1">{block.locked && !readOnly && <Lock size={9} data-testid={`banner-block-locked-${idx}`} />}{block.label || "Sperrbereich"}</span>
      {selected && !block.locked && <Handle onDown={(e) => onDown(e, "resize")} testid={`banner-block-resize-${idx}`} />}
    </div>
  );
}
